import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { get } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { Button2026 } from 'shared-components'
import {
  cn,
  sendCustomGAEvent,
  subscribeToMailchimpList,
} from 'shared-utilities'
import { getCurrentEnvConfig } from '../../../config'
import RegisterContent from '../../content/register'
import { usePathPrefix } from '../../hooks/usePathPrefix'
import { AppContext } from '../../providers/AppContext/AppContext'
import LinkComponent from '../LinkComponent/LinkComponent'
import './Register.css'
import { FormButton } from './RegisterForms/FormButton'
import OptionalDetailsForm, {
  IOption,
  OptionType,
} from './RegisterForms/OptionalDetailsForm'
import TermsAcceptForm, {
  TermsSelection,
} from './RegisterForms/TermsAcceptForm'
import UserDetailsForm from './RegisterForms/UserDetailsForm'

const config = getCurrentEnvConfig()

type RegisterButtonVariant = 'primary' | 'outlined' | 'text'
type RegisterCtaState = 'registered' | 'signedIn' | 'signedOut' | 'resolving'
export type RegisterTrackingLocation =
  | 'navbar'
  | 'home_banner'
  | 'home_about'
  | 'home_video'
  | 'home_coding'
  | 'prizes'
  | 'get_ready'

type RegisterCtaConfig = {
  variant: RegisterButtonVariant
  label: string
  href?: string
  onClick?: () => void
  disabled?: boolean
  testId?: string
}

const getRegisterCtaState = ({
  isRegistered,
  isSignedIn,
  isResolving,
}: {
  isRegistered: boolean
  isSignedIn: boolean
  isResolving: boolean
}): RegisterCtaState => {
  if (isRegistered) return 'registered'
  if (isResolving) return 'resolving'
  if (isSignedIn) return 'signedIn'
  return 'signedOut'
}

export const REGISTER_QUERY_PARAM = 'register'

export const buildRegisterReturnTo = (
  pathname: string,
  search = '',
  pathPrefix = ''
): string => {
  const unprefixedPath =
    pathPrefix && pathname.startsWith(pathPrefix)
      ? pathname.slice(pathPrefix.length) || '/'
      : pathname || '/'
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )
  params.set(REGISTER_QUERY_PARAM, 'refer')
  return `${unprefixedPath}?${params.toString()}`
}

// Allow only alphanumeric and underscore characters
const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/

export const GET_PLAYER_DETAILS = gql(`
  query GetPlayerCurrent {
    player {
      id
      team {
        id
        name
      }
    }
  }
`)

export const GET_EVENT_DETAILS = gql(`
  query GetEventDetails {
    events {
      id
      slug
      status
      registrationOpen
      teamsLocked
    }
  }
`)

export const CREATE_PLAYER = gql(`
  # Registers new users
  mutation PlayerCreate($meta: JSON!) {
    playerCreate(meta: $meta) {
      player {
        id
      }
      userError {
        field
        message
      }
    }
  }
`)

export type Event = {
  id: string
  registrationOpen: boolean
  slug: string
  status: string
  teamsLocked: boolean
}

type SubmitError = {
  field?: string
  message: string
}

export type UserInformationObject = {
  referredBy: string
  country: string
  education: string
  experience: string
  referralSource: IOption[]
  framework: IOption[]
  attendQHackBefore: string
  termsAccepted: boolean
  newsletterSubscribed: boolean
  electronicCommunicationAccepted: boolean
}

enum RegisterStep {
  UserDetails = 0,
  OptionalDetails = 1,
  TermsAccept = 2,
  Success = 3,
}

const { title, registrationSuccess, registeredUserCTA, registerButtonLabel } =
  RegisterContent
const { invalidUsernameError } = RegisterContent.userDetailsForm

const Register = ({
  showOnlyCTAWithoutModal = false,
  buttonVariant = 'primary',
  buttonClassName,
  buttonIcon,
  registeredContent,
  trackingLocation,
}: {
  showOnlyCTAWithoutModal?: boolean
  buttonVariant?: RegisterButtonVariant
  buttonIcon?: React.ReactNode
  buttonClassName?: string
  registeredContent?: React.ReactNode
  trackingLocation?: RegisterTrackingLocation
}) => {
  const {
    user,
    isAuthenticated,
    loginWithRedirect,
    isLoading: isAuthLoading,
  } = useAuth0()
  const pathPrefix = usePathPrefix()
  const {
    eventDetails,
    setEventDetails,
    playerDetails,
    setPlayerDetails,
    showRegisterModal,
    setShowRegisterModal,
    setEventDetailsLoaded,
  } = useContext(AppContext)

  const [currentStep, setCurrentStep] = useState(RegisterStep.UserDetails)
  const [usernameCharactersValid, setUsernameCharactersValid] = useState(true)
  const [, setIsQueryError] = useState(false)

  const [userInformation, setUserInformation] = useState<UserInformationObject>(
    {
      referredBy: '',
      country: '',
      education: '',
      experience: '',
      referralSource: [],
      framework: [],
      attendQHackBefore: '',
      termsAccepted: false,
      newsletterSubscribed: false,
      electronicCommunicationAccepted: false,
    }
  )
  const [submitErrors, setSubmitErrors] = useState<SubmitError[] | null>(null)

  const [getEventDetails, { data, error }] = useLazyQuery(GET_EVENT_DETAILS)

  const [
    getPlayerData,
    {
      data: playerData,
      error: playerError,
      loading: PlayerDataLoading,
      called: playerDataCalled,
    },
  ] = useLazyQuery(GET_PLAYER_DETAILS, {
    context: {
      headers: {
        'X-Event-ID': eventDetails.id,
      },
    },
  })

  const [createPlayer] = useMutation(CREATE_PLAYER, {
    context: {
      headers: {
        'X-Event-ID': eventDetails.id,
      },
    },
  })

  useEffect(() => {
    if (!eventDetails.id) {
      getEventDetails()
    }
  }, [])

  useEffect(() => {
    if (data) {
      if (data.events && data.events.length) {
        data.events.forEach((event: Event) => {
          if (event.slug === config.activeEventSlug) {
            setEventDetails({ ...event })
          }
        })
      }
      setEventDetailsLoaded(true)
    }
    if (error) {
      setIsQueryError(true)
      setEventDetailsLoaded(true)
    }
  }, [data, error])

  useEffect(() => {
    if (playerData && playerData.player && playerData.player.id) {
      setPlayerDetails({
        ...playerDetails,
        id: playerData.player.id || '',
        team: playerData.player.team
          ? {
              id: playerData.player.team.id,
              name: playerData.player.team.name,
            }
          : null,
      })
    }
    if (playerError) {
      setIsQueryError(true)
    }
  }, [playerData, playerError])

  useEffect(() => {
    if (
      !!eventDetails.id &&
      !playerDetails.id &&
      !isAuthLoading &&
      isAuthenticated
    ) {
      if (!PlayerDataLoading) {
        getPlayerData()
      }
    }
  }, [eventDetails.id, playerDetails.id, isAuthLoading, isAuthenticated])

  const registerUserForEvent = async () => {
    setSubmitErrors(null)
    if (eventDetails.registrationOpen && eventDetails.status !== 'FINISHED') {
      try {
        const response = await createPlayer({
          variables: {
            meta: userInformation,
          },
        })
        setPlayerDetails({
          ...playerDetails,
          ...get(response, 'data.playerCreate.player', {}),
          team: playerDetails.team,
        })
        if (
          userInformation.newsletterSubscribed &&
          config.newsletterMailchimp.baseURL &&
          config.newsletterMailchimp.mailingList
        ) {
          subscribeToMailchimpList(
            { EMAIL: user?.email },
            config.newsletterMailchimp.baseURL,
            config.newsletterMailchimp.mailingList
          )
        }
        if (
          userInformation.electronicCommunicationAccepted &&
          config.welcomeEmailMailchimp.baseURL &&
          config.welcomeEmailMailchimp.mailingList
        ) {
          subscribeToMailchimpList(
            { EMAIL: user?.email },
            config.welcomeEmailMailchimp.baseURL,
            config.welcomeEmailMailchimp.mailingList
          )
        }
        const userError = get(response, 'data.playerCreate.userError')
        if (userError) setSubmitErrors([userError])
        else {
          setCurrentStep(RegisterStep.Success)
        }
      } catch {
        setSubmitErrors([
          {
            message:
              'There was an error on the server, please try again later.',
          },
        ])
      }
    }
  }

  const trackRegisterClick = (trackingLocation?: RegisterTrackingLocation) => {
    if (!trackingLocation) return
    sendCustomGAEvent('click', {
      event_category: 'qhack_2026',
      event_label: 'register_now',
      value: trackingLocation,
    })
  }

  const closeRegisterModal = () => {
    if (typeof window !== 'undefined' && 'URLSearchParams' in window) {
      const url = new URL(window.location.href)
      url.searchParams.delete(REGISTER_QUERY_PARAM)
      history.replaceState({}, '', url.href)
    }
    setShowRegisterModal(false)
  }

  const registeredCta = eventDetails.teamsLocked
    ? registeredUserCTA.eventNotStarted
    : registeredUserCTA.eventStarted

  const playerId = playerDetails.id || playerData?.player?.id || null

  const isRegistrationResolving =
    isAuthLoading ||
    (isAuthenticated &&
      !playerId &&
      (!eventDetails.id ? !error : !playerDataCalled || PlayerDataLoading))

  const registerCtaState = getRegisterCtaState({
    isRegistered: Boolean(playerId),
    isSignedIn: isAuthenticated,
    isResolving: isRegistrationResolving,
  })

  useEffect(() => {
    if (registerCtaState !== 'signedIn' || !eventDetails.registrationOpen) {
      return
    }
    if (typeof window === 'undefined') return
    if (
      !new URLSearchParams(window.location.search).has(REGISTER_QUERY_PARAM)
    ) {
      return
    }
    setShowRegisterModal(true)
  }, [registerCtaState, eventDetails.registrationOpen])

  // Update URL to support analytics tracking
  useEffect(() => {
    if (typeof window === 'undefined' || !('URLSearchParams' in window)) return
    const url = new URL(window.location.href)
    if (showRegisterModal) {
      if (currentStep === RegisterStep.UserDetails) {
        url.searchParams.set(REGISTER_QUERY_PARAM, 'refer')
      }
      if (currentStep === RegisterStep.OptionalDetails) {
        url.searchParams.set(REGISTER_QUERY_PARAM, 'optional-details')
      }
      if (currentStep === RegisterStep.TermsAccept) {
        url.searchParams.set(REGISTER_QUERY_PARAM, 'accept-terms')
      }
      history.pushState({}, '', url.href)
    } else if (registerCtaState === 'registered') {
      url.searchParams.delete(REGISTER_QUERY_PARAM)
      history.pushState({}, '', url.href)
    }
    setSubmitErrors(null)
  }, [currentStep, showRegisterModal, registerCtaState])

  const registerCtaConfig: Record<RegisterCtaState, RegisterCtaConfig> = {
    registered: {
      variant: buttonVariant,
      label: registeredCta.label,
      href: registeredCta.link,
    },
    signedIn: {
      variant: buttonVariant,
      label: registerButtonLabel.loggedIn,
      onClick: () => {
        if (!eventDetails.registrationOpen) return
        setShowRegisterModal(true)
        trackRegisterClick(trackingLocation)
      },
      disabled: !eventDetails.registrationOpen || PlayerDataLoading,
      testId: 'signed-in-non-registered-user-button',
    },
    signedOut: {
      variant: buttonVariant,
      label: registerButtonLabel.loggedOut,
      onClick: () => {
        if (typeof window === 'undefined') return
        trackRegisterClick(trackingLocation)
        loginWithRedirect({
          appState: {
            returnTo: buildRegisterReturnTo(
              window.location.pathname,
              window.location.search,
              pathPrefix
            ),
          },
        })
      },
      testId: 'signed-out-non-registered-user-button',
    },
    resolving: {
      variant: buttonVariant,
      label: 'Loading...',
      disabled: true,
      testId: 'register-cta-resolving',
    },
  }

  const { label, testId, ...registerCtaButtonProps } =
    registerCtaConfig[registerCtaState]

  if (eventDetails.status === 'FINISHED') {
    return null
  }

  const components: Record<RegisterStep, React.ReactNode> = {
    [RegisterStep.UserDetails]: (
      <UserDetailsForm
        referredBy={userInformation.referredBy}
        usernameCharactersValid={usernameCharactersValid}
        onUsernameUpdate={(referredBy: string) => {
          setUserInformation({ ...userInformation, referredBy })
          if (referredBy) {
            const usernameCharactersValid = new RegExp(USERNAME_REGEX).test(
              referredBy
            )
            setUsernameCharactersValid(usernameCharactersValid)
            if (!usernameCharactersValid) {
              setSubmitErrors([{ message: invalidUsernameError }])
            } else {
              setSubmitErrors(null)
            }
          } else {
            setSubmitErrors(null)
            setUsernameCharactersValid(true)
          }
        }}
        goToNextStep={() => setCurrentStep(RegisterStep.OptionalDetails)}
        closeRegistration={closeRegisterModal}
      />
    ),
    [RegisterStep.OptionalDetails]: (
      <OptionalDetailsForm
        userInformation={userInformation}
        goToPreviousStep={() => setCurrentStep(RegisterStep.UserDetails)}
        onUpdateAnswers={(optionalAnswers: OptionType) => {
          setUserInformation({ ...userInformation, ...optionalAnswers })
          setCurrentStep(RegisterStep.TermsAccept)
        }}
      />
    ),
    [RegisterStep.TermsAccept]: (
      <TermsAcceptForm
        userInformation={userInformation}
        goToPreviousStep={() => setCurrentStep(RegisterStep.OptionalDetails)}
        updateSelection={(selectedTerms: TermsSelection) =>
          setUserInformation({ ...userInformation, ...selectedTerms })
        }
        registerUser={() => {
          registerUserForEvent()
        }}
      />
    ),
    [RegisterStep.Success]: (
      <FormButton
        onClick={closeRegisterModal}
        className="mt-10 mb-6 w-[330px] mx-auto"
      >
        {registrationSuccess.buttonLabel}
      </FormButton>
    ),
  }

  return (
    <>
      {registeredContent && registerCtaState === 'registered' ? (
        registeredContent
      ) : (
        <Button2026
          LinkComponent={LinkComponent}
          {...registerCtaButtonProps}
          data-testid={testId}
          aria-busy={registerCtaState === 'resolving'}
          aria-label={
            registerCtaState === 'resolving'
              ? 'Loading registration'
              : undefined
          }
          className={buttonClassName}
          icon={registerCtaState === 'resolving' ? null : buttonIcon}
        >
          {label}
        </Button2026>
      )}
      {showOnlyCTAWithoutModal ? null : (
        <Modal
          isOpen={showRegisterModal}
          onRequestClose={closeRegisterModal}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          className="Register__modal"
          id="register-modal"
          ariaHideApp={false}
          testId="register-modal"
          aria={{
            labelledby: 'register-modal-title',
          }}
        >
          <CloseButton onClick={closeRegisterModal} />

          <h2
            id="register-modal-title"
            className={cn(
              'font-sora  text-2026-form-navy font-bold text-center',
              currentStep === RegisterStep.Success
                ? 'text-[31px] mt-6'
                : 'text-[30px] uppercase'
            )}
          >
            {currentStep === RegisterStep.Success
              ? registrationSuccess.title
              : title}
          </h2>

          {components[currentStep]}

          {submitErrors && submitErrors.length && (
            <div
              className="mb-4 mt-8 text-sm text-2026-form-error"
              data-testid="register-form-errors"
            >
              <p className="font-semibold mb-2">
                The following error(s) occurred:
              </p>
              <ul className="list-disc pl-4">
                {submitErrors.map((error, index) => (
                  <li key={`error-${index}`}>{error.message}</li>
                ))}
              </ul>
            </div>
          )}
        </Modal>
      )}
    </>
  )
}

export default Register

const CloseButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <button
      className="absolute top-4 right-[19px] inline mt-0 size-6"
      aria-label="close registration modal"
      onClick={onClick}
    >
      <i className="bx bx-x text-2xl"></i>
    </button>
  )
}
